// Funciones puras del supervisor: formato de porcentaje, clases de
// badge por umbral, y resumen agregado de un set de choferes.
// Aisladas del componente para permitir tests unitarios sin DOM.

import { driverGpsFresh, driverAlert, type LiveVehicle } from "./useLiveFleet";

/** "41.57%" — siempre 2 decimales; "—" si el valor no es finito. */
export function formatPct(pct: number): string {
  if (!Number.isFinite(pct)) return "—";
  return `${pct.toFixed(2)}%`;
}

/** Tailwind classes para el badge de % Ef. Visita.
 *
 * Umbrales operativos:
 *   >= 80%  → verde   (state-delivered)
 *   60–79%  → ámbar   (state-delayed)
 *   < 60%   → rojo    (state-failed)
 *
 * Devuelve también un identificador legible del tier para tests / UI
 * alternativa (sin tener que parsear las classes).
 */
export type PctTier = "good" | "warn" | "bad";

export function pctTier(pct: number): PctTier {
  if (pct >= 80) return "good";
  if (pct >= 60) return "warn";
  return "bad";
}

export function pctBadgeClasses(pct: number): string {
  switch (pctTier(pct)) {
    case "good":
      return "bg-state-delivered/15 text-state-delivered border border-state-delivered/40";
    case "warn":
      return "bg-state-delayed/15 text-state-delayed border border-state-delayed/40";
    case "bad":
      return "bg-state-failed/15 text-state-failed border border-state-failed/40";
  }
}

/** KPIs globales del supervisor.
 *
 *   stops      = todas las paradas planificadas
 *   visited    = paradas con cualquier cierre (delivered / partial / failed / skipped)
 *   done       = entregadas (delivered + partial)
 *   failed     = rechazadas/saltadas (failed + skipped)
 *   programadas= cantidad de choferes con ruta
 *   active     = choferes con GPS fresco (<5 min)
 *   alerts     = choferes con alguna alerta activa
 *   efVisita   = visited/stops × 100
 *   efEntrega  = done/stops × 100
 */
export type FleetTotals = {
  stops: number;
  visited: number;
  done: number;
  failed: number;
  programadas: number;
  active: number;
  alerts: number;
  efVisita: number;
  efEntrega: number;
};

export function fleetTotals(vehicles: LiveVehicle[]): FleetTotals {
  let stops = 0,
    visited = 0,
    done = 0,
    failed = 0,
    active = 0,
    alerts = 0;
  for (const v of vehicles) {
    stops += v.stopsTotal;
    visited += v.stopsVisited;
    done += v.stopsDone;
    failed += v.stopsFailed;
    if (driverGpsFresh(v)) active++;
    if (driverAlert(v) !== "none") alerts++;
  }
  return {
    stops,
    visited,
    done,
    failed,
    programadas: vehicles.length,
    active,
    alerts,
    efVisita: stops === 0 ? 0 : (visited / stops) * 100,
    efEntrega: stops === 0 ? 0 : (done / stops) * 100,
  };
}

/** % Ef. Visita y Ef. Entrega por vehículo. Espejo de las fórmulas globales. */
export function driverPcts(v: LiveVehicle): { efVisita: number; efEntrega: number } {
  if (v.stopsTotal === 0) return { efVisita: 0, efEntrega: 0 };
  return {
    efVisita: (v.stopsVisited / v.stopsTotal) * 100,
    efEntrega: (v.stopsDone / v.stopsTotal) * 100,
  };
}
